import { computed, effect, inject, Injectable, signal } from '@angular/core';
import { SettingsService } from '@infodata-group/inf-commons';
import { PreferencesSettingsKeyEnum } from '../enum/preferences-settings-key.enum';
import { SETTINGS_DEFAULT_VALUES } from '../engine/app.constants';

@Injectable({
  providedIn: 'root',
})
export class SharedMapSettingsService {
  private readonly settingsService = inject(SettingsService);

  public isClustered = signal(
    SETTINGS_DEFAULT_VALUES['preferences-map-options-clustered'],
  );
  public isTrafficShown = signal(
    SETTINGS_DEFAULT_VALUES['preferences-map-options-traffic'],
  );
  public mapTypeControl = signal<string>(
    SETTINGS_DEFAULT_VALUES['preferences-map-options-control'],
  );
  public mapThemeId = signal<string>(
    SETTINGS_DEFAULT_VALUES['preferences-gmaps-theme'],
  );
  public mapThemeGoogleId = computed(() => {
    const data = {
      MAP_001: '4b1d8d4fd94dc567940c9618',
      MAP_002: '4b1d8d4fd94dc56772850cbd',
      MAP_003: '4b1d8d4fd94dc567b4f7434d',
      MAP_004: '4b1d8d4fd94dc567d2545c97',
      MAP_005: '4b1d8d4fd94dc567b1baa223',
      MAP_006: '4b1d8d4fd94dc567a1d77b63',
      MAP_007: '4b1d8d4fd94dc567faec2adc',
      MAP_008: 'MAP_008',
    };
    return data[this.mapThemeId()] || '';
  });
  public refreshSpeed = signal<number>(
    SETTINGS_DEFAULT_VALUES['preferences-refresh-speed'],
  );
  public defaultZoom = signal<number>(
    SETTINGS_DEFAULT_VALUES['preferences-zoom'],
  );
  public markerSize = signal<number>(
    SETTINGS_DEFAULT_VALUES['preferences-marker-size'],
  );
  public blackboxInfoWindow = signal<string[]>(
    SETTINGS_DEFAULT_VALUES['preferences-blackbox-info-window'],
  );
  public materialsInfoWindow = signal<string[]>(
    SETTINGS_DEFAULT_VALUES['preferences-materials-info-window'],
  );

  constructor() {
    this.initSettings();
    effect(() => {
      const key = PreferencesSettingsKeyEnum.isClustered;
      this.hasSettingChange(key, this.isClustered());
    });
    effect(() => {
      const key = PreferencesSettingsKeyEnum.typeControl;
      this.hasSettingChange(key, this.mapTypeControl());
    });
    effect(() => {
      const key = PreferencesSettingsKeyEnum.isTrafficShown;
      this.hasSettingChange(key, this.isTrafficShown());
    });
    effect(() => {
      const key = PreferencesSettingsKeyEnum.gmapsTheme;
      this.hasSettingChange(key, this.mapThemeId());
    });
    effect(() => {
      const key = PreferencesSettingsKeyEnum.refreshSpeed;
      this.hasSettingChange(key, this.refreshSpeed());
    });
    effect(() => {
      const key = PreferencesSettingsKeyEnum.defaultZoom;
      this.hasSettingChange(key, this.defaultZoom());
    });
    effect(() => {
      const key = PreferencesSettingsKeyEnum.markerSize;
      this.hasSettingChange(key, this.markerSize());
    });
    effect(() => {
      const key = PreferencesSettingsKeyEnum.blackboxInfoWindow;
      this.hasSettingChange(key, this.blackboxInfoWindow());
    });
    effect(() => {
      const key = PreferencesSettingsKeyEnum.materialsInfoWindow;
      this.hasSettingChange(key, this.materialsInfoWindow());
    });
  }

  public initSettings() {
    Object.values(PreferencesSettingsKeyEnum).forEach((key) => {
      const value = this.settingsService.getSetting(key);
      if (value !== undefined) {
        switch (key) {
          case PreferencesSettingsKeyEnum.isClustered:
            this.isClustered.set(value);
            break;
          case PreferencesSettingsKeyEnum.isTrafficShown:
            this.isTrafficShown.set(value);
            break;
          case PreferencesSettingsKeyEnum.typeControl:
            this.mapTypeControl.set(value);
            break;
          case PreferencesSettingsKeyEnum.gmapsTheme:
            this.mapThemeId.set(value);
            break;
          case PreferencesSettingsKeyEnum.refreshSpeed:
            this.refreshSpeed.set(value);
            break;
          case PreferencesSettingsKeyEnum.defaultZoom:
            this.defaultZoom.set(value);
            break;
          case PreferencesSettingsKeyEnum.markerSize:
            this.markerSize.set(value);
            break;
          case PreferencesSettingsKeyEnum.blackboxInfoWindow:
            this.blackboxInfoWindow.set(value);
            break;
          case PreferencesSettingsKeyEnum.materialsInfoWindow:
            this.materialsInfoWindow.set(value);
            break;
          default:
            console.warn(`Unknown setting key: ${key}`);
            break;
        }
      }
    });
  }

  private hasSettingChange(key: string, currentValue: any): void {
    const currentValueSettings = this.settingsService.getSetting(key);
    // If not exist in settings, do nothing (settings probably not initialized)
    if (currentValueSettings === undefined) {
      return;
    }
    // If the value is the same as the current setting, do nothing
    if (currentValueSettings === currentValue) {
      return;
    }
    this.settingsService.addSetting(key, currentValue);
  }
}
